import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, CreditCard, Calendar, Info } from 'lucide-react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import Navigation from '@/components/Navigation';
import { toast } from 'sonner';
import { usePlaidLink } from 'react-plaid-link';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ExpenseTracking = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [linkToken, setLinkToken] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // CPI-aligned expense templates
  const expenseTemplates = {
    cpi: {
      name: "CPI (Consumer Price Index)",
      description: "Track all goods & services",
      categories: [
        { category: "Food", items: ["Groceries", "Eggs", "Milk", "Bread", "Meat", "Fruits & Vegetables"] },
        { category: "Energy", items: ["Gasoline", "Electricity", "Natural Gas", "Heating Oil"] },
        { category: "Housing", items: ["Rent", "Mortgage", "Home Insurance", "Property Tax"] },
        { category: "Transport", items: ["Car Payment", "Auto Insurance", "Public Transit", "Car Maintenance"] },
        { category: "Healthcare", items: ["Health Insurance", "Doctor Visit", "Prescription Drugs", "Dental Care"] },
        { category: "Utilities", items: ["Water", "Trash Collection", "Internet", "Phone Bill"] }
      ]
    },
    coreCPI: {
      name: "Core CPI",
      description: "Excludes food & energy (more stable)",
      categories: [
        { category: "Housing", items: ["Rent", "Mortgage", "Home Insurance", "Property Tax", "Furniture"] },
        { category: "Transport", items: ["Car Payment", "Auto Insurance", "Public Transit", "Car Maintenance"] },
        { category: "Healthcare", items: ["Health Insurance", "Doctor Visit", "Prescription Drugs", "Dental Care"] },
        { category: "Utilities", items: ["Water", "Trash Collection", "Internet", "Phone Bill"] },
        { category: "Apparel", items: ["Clothing", "Shoes", "Accessories"] },
        { category: "Recreation", items: ["Gym Membership", "Streaming Services", "Entertainment"] }
      ]
    },
    pce: {
      name: "PCE (Personal Consumption Expenditures)",
      description: "Federal Reserve's preferred measure",
      categories: [
        { category: "Groceries", items: ["Food at Home", "Beverages", "Snacks"] },
        { category: "Dining Out", items: ["Restaurants", "Fast Food", "Cafes", "Delivery"] },
        { category: "Housing", items: ["Rent", "Mortgage", "Utilities", "Maintenance"] },
        { category: "Healthcare", items: ["Medical Services", "Prescription Drugs", "Health Insurance"] },
        { category: "Transport", items: ["Vehicle Purchase", "Gasoline", "Auto Insurance", "Public Transit"] },
        { category: "Recreation", items: ["Entertainment", "Hotels", "Sports & Hobbies"] }
      ]
    }
  };

  useEffect(() => {
    fetchExpenses();
    fetchLinkToken();
  }, []);

  const fetchExpenses = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/expenses`, {
        withCredentials: true
      });
      setExpenses(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const fetchLinkToken = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/plaid/link-token`, {}, {
        withCredentials: true
      });
      
      // Check if we're in demo mode
      if (response.data.demo_mode) {
        setIsDemoMode(true);
        setLinkToken(null);
        console.log('Plaid is in demo mode - no API keys configured');
      } else {
        setLinkToken(response.data.link_token);
        setIsDemoMode(false);
      }
    } catch (error) {
      console.error('Error fetching link token:', error);
      toast.error('Failed to initialize Plaid connection');
    }
  };

  const handlePlaidSuccess = async (publicToken) => {
    try {
      await axios.post(`${BACKEND_URL}/api/plaid/exchange-token`, 
        { public_token: publicToken },
        { withCredentials: true }
      );
      toast.success('Bank account connected successfully!');
    } catch (error) {
      console.error('Error exchanging token:', error);
      toast.error('Failed to connect bank account');
    }
  };

  const { open: openPlaid, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: handlePlaidSuccess,
    onExit: (err, metadata) => {
      if (err) {
        console.error('Plaid exit error:', err);
        toast.error('Plaid connection cancelled or failed');
      }
    }
  });

  const handlePlaidClick = () => {
    if (isDemoMode) {
      toast.info('Demo Mode: Plaid integration requires API keys. Please use manual entry for now.', {
        duration: 5000,
        description: 'To enable bank connections, add your Plaid API keys to the backend environment variables.'
      });
    } else if (ready && linkToken) {
      openPlaid();
    } else {
      toast.error('Plaid is not ready yet. Please wait a moment and try again.');
    }
  };

  const deleteExpense = async (expenseId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/expenses/${expenseId}`, {
        withCredentials: true
      });
      setExpenses(expenses.filter(e => e.expense_id !== expenseId));
      toast.success('Expense deleted');
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast.error('Failed to delete expense');
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="expense-tracking">
      <Navigation />
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8"
        >
          <h1 className="font-heading font-black text-4xl md:text-5xl mb-2">Track Your Expenses</h1>
          <p className="text-muted-foreground">Add expenses manually or connect your bank accounts</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Card className="bg-card border-card-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="w-5 h-5 text-primary" />
                  Manual Entry
                </CardTitle>
                <CardDescription>Add expenses you want to track</CardDescription>
              </CardHeader>
              <CardContent>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-expense-btn" className="w-full bg-primary hover:bg-primary/90">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Expense
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-card border-card-border">
                    <AddExpenseForm 
                      onSuccess={() => {
                        fetchExpenses();
                        setDialogOpen(false);
                        setSelectedTemplate(null);
                      }}
                      templateKey={selectedTemplate}
                      templates={expenseTemplates}
                    />
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <Card className="bg-card border-card-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-secondary" />
                  Connect Bank Account
                </CardTitle>
                <CardDescription>Automatically sync transactions</CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  onClick={handlePlaidClick}
                  variant="outline"
                  className="w-full border-card-border hover:border-secondary mb-2"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {isDemoMode ? 'Plaid (Demo Mode)' : linkToken ? 'Connect with Plaid' : 'Loading...'}
                </Button>
                {isDemoMode && (
                  <div className="bg-accent/10 border border-accent/30 rounded-lg p-3 mt-2">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-muted-foreground">
                        <p className="font-bold text-accent mb-1">Demo Mode Active</p>
                        <p>Plaid integration requires API keys from <a href="https://plaid.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">plaid.com</a>.</p>
                        <p className="mt-1">Use manual entry to track your expenses for now.</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Expense Templates Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="mb-8"
        >
          <Card className="bg-card border-card-border">
            <CardHeader>
              <CardTitle>Quick Add Templates</CardTitle>
              <CardDescription>Pre-defined categories matching official inflation metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(expenseTemplates).map(([key, template]) => (
                  <Card 
                    key={key}
                    className="bg-background border-card-border hover:border-primary/50 transition-colors cursor-pointer"
                    onClick={() => setSelectedTemplate(key)}
                  >
                    <CardContent className="p-4">
                      <h4 className="font-bold mb-1">{template.name}</h4>
                      <p className="text-xs text-muted-foreground mb-3">{template.description}</p>
                      <div className="space-y-1">
                        {template.categories.slice(0, 3).map((cat, idx) => (
                          <div key={idx} className="text-xs text-muted-foreground">
                            • {cat.category}: {cat.items.slice(0, 2).join(", ")}...
                          </div>
                        ))}
                      </div>
                      <Button 
                        size="sm" 
                        className="w-full mt-3 bg-primary/20 hover:bg-primary/30 text-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTemplate(key);
                          setDialogOpen(true);
                        }}
                      >
                        Use Template
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
        >
          <Card className="bg-card border-card-border">
            <CardHeader>
              <CardTitle>Recent Expenses</CardTitle>
              <CardDescription>{expenses.length} expenses tracked</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                </div>
              ) : expenses.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No expenses yet. Add your first expense to get started!
                </div>
              ) : (
                <div className="space-y-3">
                  {expenses.map((expense) => (
                    <div 
                      key={expense.expense_id}
                      className="flex items-center justify-between p-4 border border-card-border rounded-lg hover:border-primary/50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h4 className="font-bold">{expense.item_name}</h4>
                          <Badge variant="outline">{expense.category}</Badge>
                          {expense.recurring && <Badge className="bg-primary text-white">Recurring</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {new Date(expense.date).toLocaleDateString()} • {expense.currency}
                        </p>
                        {expense.notes && (
                          <p className="text-sm text-muted-foreground mt-1">{expense.notes}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-xl font-heading font-bold">
                          {expense.currency === 'USD' && '$'}
                          {expense.currency === 'EUR' && '€'}
                          {expense.currency === 'HKD' && 'HK$'}
                          {expense.amount.toFixed(2)}
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => deleteExpense(expense.expense_id)}
                          className="text-destructive hover:text-destructive/80"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

const AddExpenseForm = ({ onSuccess, templateKey, templates }) => {
  const [formData, setFormData] = useState({
    category: '',
    item_name: '',
    amount: '',
    currency: 'USD',
    notes: '',
    recurring: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [selectedCategoryItems, setSelectedCategoryItems] = useState([]);

  // Get template categories if template is selected
  const templateCategories = templateKey && templates ? templates[templateKey]?.categories || [] : [];

  // Update available items when category changes
  const handleCategoryChange = (newCategory) => {
    setFormData({...formData, category: newCategory, item_name: ''});
    
    if (templateKey && templates) {
      const categoryData = templates[templateKey].categories.find(c => c.category === newCategory);
      setSelectedCategoryItems(categoryData?.items || []);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(
        `${BACKEND_URL}/api/expenses`,
        { ...formData, amount: parseFloat(formData.amount) },
        { withCredentials: true }
      );
      toast.success('Expense added successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error adding expense:', error);
      toast.error('Failed to add expense');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <DialogHeader>
        <DialogTitle>Add New Expense</DialogTitle>
        <DialogDescription>
          {templateKey && templates ? 
            `Using ${templates[templateKey].name} template - ${templates[templateKey].description}` :
            'Track items you buy regularly to monitor price changes'
          }
        </DialogDescription>
      </DialogHeader>

      <div>
        <Label htmlFor="category">Category</Label>
        <Select value={formData.category} onValueChange={handleCategoryChange}>
          <SelectTrigger className="bg-background border-card-border">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent className="bg-card border-card-border">
            {templateKey && templateCategories.length > 0 ? (
              // Template-specific categories
              templateCategories.map((cat) => (
                <SelectItem key={cat.category} value={cat.category}>
                  {cat.category}
                </SelectItem>
              ))
            ) : (
              // Default categories
              <>
                <SelectItem value="Food">Food</SelectItem>
                <SelectItem value="Groceries">Groceries</SelectItem>
                <SelectItem value="Transport">Transport</SelectItem>
                <SelectItem value="Utilities">Utilities</SelectItem>
                <SelectItem value="Housing">Housing</SelectItem>
                <SelectItem value="Healthcare">Healthcare</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </>
            )}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="item_name">Item Name</Label>
        {selectedCategoryItems.length > 0 ? (
          <Select value={formData.item_name} onValueChange={(value) => setFormData({...formData, item_name: value})}>
            <SelectTrigger className="bg-background border-card-border">
              <SelectValue placeholder="Select item or type custom" />
            </SelectTrigger>
            <SelectContent className="bg-card border-card-border">
              {selectedCategoryItems.map((item) => (
                <SelectItem key={item} value={item}>
                  {item}
                </SelectItem>
              ))}
              <SelectItem value="__custom__">Custom (type below)</SelectItem>
            </SelectContent>
          </Select>
        ) : null}
        {(selectedCategoryItems.length === 0 || formData.item_name === '__custom__') && (
          <Input
            id="item_name"
            placeholder="e.g., Eggs, Pasta, Rice"
            value={formData.item_name === '__custom__' ? '' : formData.item_name}
            onChange={(e) => setFormData({...formData, item_name: e.target.value})}
            required
            className="bg-background border-card-border mt-2"
          />
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="amount">Amount</Label>
          <Input
            id="amount"
            type="number"
            step="0.01"
            placeholder="0.00"
            value={formData.amount}
            onChange={(e) => setFormData({...formData, amount: e.target.value})}
            required
            className="bg-background border-card-border"
          />
        </div>
        <div>
          <Label htmlFor="currency">Currency</Label>
          <Select value={formData.currency} onValueChange={(value) => setFormData({...formData, currency: value})}>
            <SelectTrigger className="bg-background border-card-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-card-border">
              <SelectItem value="USD">USD</SelectItem>
              <SelectItem value="EUR">EUR</SelectItem>
              <SelectItem value="HKD">HKD</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label htmlFor="notes">Notes (optional)</Label>
        <Input
          id="notes"
          placeholder="Any additional details"
          value={formData.notes}
          onChange={(e) => setFormData({...formData, notes: e.target.value})}
          className="bg-background border-card-border"
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="recurring"
          checked={formData.recurring}
          onChange={(e) => setFormData({...formData, recurring: e.target.checked})}
          className="rounded"
        />
        <Label htmlFor="recurring" className="cursor-pointer">This is a recurring purchase</Label>
      </div>

      <Button 
        type="submit" 
        className="w-full bg-primary hover:bg-primary/90"
        disabled={submitting}
      >
        {submitting ? 'Adding...' : 'Add Expense'}
      </Button>
    </form>
  );
};

export default ExpenseTracking;
